import cls from "classnames"
import { AnimatePresence, motion } from "motion/react"
import { useLayoutEffect, useRef, useState } from "react"

import StrokeText from "@/components/StrokeText"
import { i18n } from "@/i18n"
import { px2rem } from "@/utils/adaptFont"
import type { CharaAvatar } from "./const"
import { getCharaAvatar, getSpeakerDecorLabel } from "./const"

import styles from "./index.module.scss"

interface DialogBoxProps {
    leftCharacter?: string
    leftExpression?: string
    rightCharacter?: string
    rightExpression?: string
    active: "left" | "right"
    text: string
    fullText?: string
    size?: "default" | "small"
    fontSize?: number
    lineHeight?: number
    scenario?: "overlay" | "modal"
}

function avatarContainerStyle(
    avatar: CharaAvatar,
    side: "left" | "right",
): React.CSSProperties | undefined {
    if (!avatar?.offsetX) return undefined
    const offsetX = side === "right" ? -avatar.offsetX : avatar.offsetX
    return {
        "--avatar-offset-x": px2rem(offsetX),
    } as React.CSSProperties
}

function avatarCropStyle(
    avatar: CharaAvatar,
    size: "default" | "small",
): React.CSSProperties | undefined {
    if (!avatar || size === "small") return undefined
    // 修改为 hidden 即可裁切，但是貌似也可以不裁切
    return { overflow: "visible", height: px2rem(avatar.height), flexShrink: 0 }
}

function avatarImgStyle(
    avatar: CharaAvatar,
    size: "default" | "small",
): React.CSSProperties | undefined {
    if (!avatar || size === "small") return undefined
    return { width: px2rem(avatar.width) }
}

export const DialogBox: React.FC<DialogBoxProps> = ({
    leftCharacter,
    leftExpression,
    rightCharacter,
    rightExpression,
    active,
    text,
    fullText,
    size = "default",
    fontSize,
    lineHeight,
    scenario = "overlay",
}) => {
    const leftAvatar = leftCharacter
        ? getCharaAvatar(leftCharacter, leftExpression)
        : undefined
    const rightAvatar = rightCharacter
        ? getCharaAvatar(rightCharacter, rightExpression)
        : undefined

    const activeCharacter = active === "left" ? leftCharacter : rightCharacter

    const measureRef = useRef<HTMLDivElement>(null)
    const [measuredLines, setMeasuredLines] = useState(0)
    const measureContent = fullText ?? text

    useLayoutEffect(() => {
        if (size !== "default") return
        const el = measureRef.current
        if (!el) return
        const lh = parseFloat(window.getComputedStyle(el).lineHeight)
        if (!lh) return
        setMeasuredLines(Math.round(el.scrollHeight / lh))
    }, [measureContent, size, leftCharacter, rightCharacter])

    let autoFontSize: number | undefined
    let autoLineHeight: number | undefined
    if (size === "default" && measuredLines > 0) {
        if (measuredLines <= 3)      { autoFontSize = 24; autoLineHeight = 32 }
        else if (measuredLines === 4) { autoFontSize = 20; autoLineHeight = 26 }
        // else if (measuredLines === 5) { autoFontSize = 16; autoLineHeight = 20 }
        else                          { autoFontSize = 18; autoLineHeight = 22 }
    }
    const resolvedFontSize = fontSize ?? autoFontSize
    const resolvedLineHeight = lineHeight ?? autoLineHeight

    return (
        <div
            className={cls(styles.dialogBox, {
                [styles.small]: size === "small",
                [styles.hasLeftAvatar]: !!leftCharacter,
                [styles.hasRightAvatar]: !!rightCharacter,
            })}
        >
            {leftCharacter && (
                <div
                    key={leftCharacter}
                    className={cls(styles.avatarLeft, {
                        [styles.avatarDimmed]: active !== "left",
                    })}
                    style={avatarContainerStyle(leftAvatar, "left")}
                >
                    {leftAvatar ? (
                        <div style={avatarCropStyle(leftAvatar, size)}>
                            <img
                                key={leftAvatar.src}
                                src={leftAvatar.src}
                                alt={leftCharacter}
                                draggable={false}
                                style={avatarImgStyle(leftAvatar, size)}
                            />
                        </div>
                    ) : (
                        <div className={styles.avatarPlaceholder} />
                    )}
                </div>
            )}

            {rightCharacter && (
                <div
                    key={rightCharacter}
                    className={cls(styles.avatarRight, {
                        [styles.avatarDimmed]: active !== "right",
                    })}
                    style={avatarContainerStyle(rightAvatar, "right")}
                >
                    {rightAvatar ? (
                        <div style={avatarCropStyle(rightAvatar, size)}>
                            <img
                                key={rightAvatar.src}
                                src={rightAvatar.src}
                                alt={rightCharacter}
                                draggable={false}
                                style={avatarImgStyle(rightAvatar, size)}
                            />
                        </div>
                    ) : (
                        <div className={styles.avatarPlaceholder} />
                    )}
                </div>
            )}

            <AnimatePresence mode="wait">
                {activeCharacter && (
                    <motion.div
                        key={activeCharacter}
                        className={styles.label}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                    >
                        <StrokeText
                            className={styles.labelDeco}
                            text={getSpeakerDecorLabel(activeCharacter)}
                            fontSize={16}
                            fontFamily="Trajan-Bold"
                            fillColor="#443122"
                            inner={{ color: "#ffffff", width: 2 }}
                            outer={{ color: "#a9885b", width: 2 }}
                            padding={2}
                        />
                        <StrokeText
                            className={styles.labelText}
                            text={i18n.get(activeCharacter as never)}
                            fontSize={36}
                            fontFamily="Georgia-Bold"
                            fillColor="#443122"
                            inner={{ color: "#ffffff", width: 3 }}
                            outer={{ color: "#f6d1aa", width: 3 }}
                            padding={2}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <div
                className={cls(styles.textContent, {
                    [styles.textContentModal]: scenario === "modal",
                })}
                style={{
                    ...(resolvedFontSize ? { fontSize: px2rem(resolvedFontSize) } : {}),
                    ...(resolvedLineHeight ? { lineHeight: px2rem(resolvedLineHeight) } : {}),
                }}
            >
                {text}
            </div>

            {size === "default" && (
                <div
                    ref={measureRef}
                    aria-hidden="true"
                    className={styles.textMeasure}
                    style={{
                        width: px2rem(
                            815 - (leftCharacter ? 150 : 0) - (rightCharacter ? 180 : 0),
                        ),
                    }}
                >
                    {measureContent}
                </div>
            )}
        </div>
    )
}
